import {
  RangeEnvironmentsPrivate,
  RangeSimEnvironmentConstants,
  RangeSimModelData,
  RangeSimRim,
  RangeSimRimEnvironmentConstants,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

const calculatePayloadValue = (𝜌: number, R: number, P: number) => 𝜌 * P * R;

const calculateTyreValue = (
  T: number,
  rimEnvironment: RangeSimRimEnvironmentConstants
) => {
  const {𝛿0, 𝛿1, 𝛿2, 𝛿3} = rimEnvironment;

  return 𝛿3 * Math.pow(T, 3) + 𝛿2 * Math.pow(T, 2) + 𝛿1 * T + 𝛿0;
};

const calculateAcConsumptionValue = (
  K: number,
  T: number,
  environment: RangeSimEnvironmentConstants
) => {
  const {Γ0, Γ1, 𝛾0, 𝛾1, 𝛾2, 𝛾3, 𝛾4} = environment;

  // AC off
  if (!K) {
    // low temperature
    if (T <= 23) {
      return 0;
    }

    return Γ1 * T + Γ0;
  }

  return (
    𝛾4 * Math.pow(T, 4) +
    𝛾3 * Math.pow(T, 3) +
    𝛾2 * Math.pow(T, 2) +
    𝛾1 * T +
    𝛾0
  );
};

const calculateBasicConsumptionValue = (
  T: number,
  environment: RangeSimEnvironmentConstants
) => {
  const {𝛼0, 𝛼1, 𝛼2, 𝛼3} = environment;

  return 𝛼3 * Math.pow(T, 3) + 𝛼2 * Math.pow(T, 2) + 𝛼1 * T + 𝛼0;
};

const calculateRangeForEnvironment = (
  /** Set of environment data for this car */
  C: RangeSimEnvironmentConstants,
  /** Environment to calculate Range for */
  E: RangeEnvironmentsPrivate,
  /** Outside temperature in degrees celsius. Possible value range: -10 to +30 */
  T: number,
  /** Boolean factor, depending on air-con / heating being on (1) or off (0) */
  K: boolean,
  /**
   * Boolean factor (if toggle), depending on payload being heavy (1) or not (0)
   * KG factor (if slider) for specific payload calculation.
   */
  P: boolean | number,
  /** data of currently selected rim  */
  R: RangeSimRim
) => {
  const payloadValue =
    typeof P === 'boolean'
      ? calculatePayloadValue(C.𝜌0, R.R, P ? 1 : 0) // Toggle
      : calculatePayloadValue(C.𝜌1, R.R, P); // Slider

  return (
    calculateBasicConsumptionValue(T, C) +
    calculateAcConsumptionValue(K ? 1 : 0, T, C) +
    calculateTyreValue(T, R.environments[E]) +
    payloadValue
  );
};

export const calculateRange = (
  /** weighting (0 - 1) per environment, weightings must add up to 1! */
  EnvironmentWeightings: Record<RangeEnvironmentsPrivate, number>,
  /** Outside temperature in degrees celsius. Possible value range: -10 to +35 */
  temperature: number,
  isAirConOrHeatingOn: boolean,
  isFasterHighwayToggleOn: boolean,
  payload: boolean | number,
  rim: RangeSimRim,
  calculationData: RangeSimModelData
): number => {
  const overAllConsumption = (
    Object.keys(EnvironmentWeightings) as RangeEnvironmentsPrivate[]
  ).reduce((sum, environment) => {
    // switch between highway and highway plus depending on faster highway toggle
    if (
      (environment === RangeEnvironmentsPrivate.motorway &&
        isFasterHighwayToggleOn) ||
      (environment === RangeEnvironmentsPrivate.motorwayPlus &&
        !isFasterHighwayToggleOn)
    ) {
      return sum;
    }

    return (
      sum +
      EnvironmentWeightings[environment] *
        calculateRangeForEnvironment(
          calculationData.environments[environment],
          environment,
          temperature,
          isAirConOrHeatingOn,
          payload,
          rim
        )
    );
  }, 0);

  const energyContent =
    calculationData.𝛽4 * Math.pow(temperature, 4) +
    calculationData.𝛽3 * Math.pow(temperature, 3) +
    calculationData.𝛽2 * Math.pow(temperature, 2) +
    calculationData.𝛽1 * temperature +
    calculationData.𝛽0;

  return (energyContent * 1000) / overAllConsumption;
};
